import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';

const Container = styled.div`
  padding: 2rem;
  background-color: #f5f5f5;
  min-height: 100vh;
`;

const Breadcrumb = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #ddd;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: #333;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  background-color: ${props => props.primary ? '#007bff' : props.danger ? '#dc3545' : props.success ? '#28a745' : '#6c757d'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background-color: ${props => props.primary ? '#0069d9' : props.danger ? '#c82333' : props.success ? '#218838' : '#5a6268'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid ${props => props.active ? '#007bff' : 'transparent'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.color || '#333'};
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-top: 0.25rem;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  font-size: 0.9rem;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const SearchInput = styled.input`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  font-size: 0.9rem;
  flex: 1;
  max-width: 300px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const BatchActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 1rem;
  background: #e7f3ff;
  border-radius: 8px;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const SelectedCount = styled.span`
  font-weight: 500;
  color: #007bff;
  margin-right: 1rem;
`;

const AnnouncementList = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const AnnouncementItem = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  background: ${props => props.selected ? '#e7f3ff' : 'white'};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${props => props.selected ? '#e7f3ff' : '#f9f9f9'};
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin-top: 0.25rem;
`;

const AnnouncementContent = styled.div`
  flex: 1;
`;

const AnnouncementHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const AnnouncementTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const TypeBadge = styled.span`
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;
  background-color: ${props => {
    switch(props.$type) {
      case 'notice': return '#17a2b8';
      case 'news': return '#28a745';
      case 'activity': return '#ffc107';
      case 'system': return '#6c757d';
      default: return '#e9ecef';
    }
  }};
  color: ${props => ['activity'].includes(props.$type) ? '#333' : 'white'};
`;

const TopBadge = styled.span`
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;
  background-color: #dc3545;
  color: white;
`;

const AnnouncementStatus = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => {
    switch(props.$status) {
      case 'published': return '#d4edda';
      case 'draft': return '#fff3cd';
      case 'archived': return '#f8d7da';
      default: return '#e9ecef';
    }
  }};
  color: ${props => {
    switch(props.$status) {
      case 'published': return '#155724';
      case 'draft': return '#856404';
      case 'archived': return '#721c24';
      default: return '#495057';
    }
  }};
`;

const AnnouncementDescription = styled.p`
  color: #666;
  margin: 0.5rem 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AnnouncementMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
  color: #888;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const AnnouncementActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 0.375rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  color: #333;
  cursor: pointer;
  font-size: 0.8rem;

  &:hover {
    background-color: #f8f9fa;
  }

  &.primary {
    background-color: #007bff;
    color: white;
    border-color: #007bff;

    &:hover {
      background-color: #0069d9;
    }
  }

  &.success {
    background-color: #28a745;
    color: white;
    border-color: #28a745;

    &:hover {
      background-color: #218838;
    }
  }

  &.danger {
    background-color: #dc3545;
    color: white;
    border-color: #dc3545;

    &:hover {
      background-color: #c82333;
    }
  }

  &.warning {
    background-color: #ffc107;
    color: #333;
    border-color: #ffc107;

    &:hover {
      background-color: #e0a800;
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #888;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #666;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #dc3545;
`;

const Toast = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 1rem 1.5rem;
  background: ${props => props.$type === 'success' ? '#28a745' : props.$type === 'error' ? '#dc3545' : '#007bff'};
  color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: ${props => props.$size === 'large' ? '900px' : '600px'};
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  margin-bottom: 1.5rem;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  resize: vertical;
  min-height: 150px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const QuillEditorWrapper = styled.div`
  .ql-container {
    min-height: 200px;
    font-size: 1rem;
  }
  .ql-editor {
    min-height: 200px;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FormCol = styled.div`
  flex: 1;
  min-width: 200px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-weight: normal;

  input {
    cursor: pointer;
  }
`;

const ContentPreview = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  line-height: 1.6;
  max-height: 300px;
  overflow-y: auto;

  h1, h2, h3, h4, h5, h6 {
    margin: 0.5em 0;
  }

  p {
    margin: 0.5em 0;
  }

  ul, ol {
    margin: 0.5em 0;
    padding-left: 1.5em;
  }

  li {
    margin: 0.25em 0;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const PageButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover:not(:disabled) {
    background: #f8f9fa;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.active {
    background: #007bff;
    color: white;
    border-color: #007bff;
  }
`;

const SelectAllContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
`;

const ConfirmModalContent = styled(ModalContent)`
  max-width: 500px;
`;

const WarningIcon = styled.div`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 1rem;
`;

const ConfirmMessage = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  font-size: 1rem;
  color: #333;
`;

const ConfirmDetails = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  max-height: 200px;
  overflow-y: auto;
`;

const ConfirmDetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
  font-size: 0.9rem;

  &:last-child {
    border-bottom: none;
  }
`;

const DeleteProgress = styled.div`
  margin-top: 1rem;
`;

const ProgressBar = styled.div`
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #dc3545 0%, #c82333 100%);
  transition: width 0.3s ease;
  width: ${props => props.percent}%;
`;

const ProgressText = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: #666;
`;

const SelectionDropdown = styled.select`
  padding: 0.375rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  font-size: 0.85rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;
  background-color: ${props => {
    switch(props.$status) {
      case 'published': return '#d4edda';
      case 'draft': return '#fff3cd';
      case 'archived': return '#f8d7da';
      default: return '#e9ecef';
    }
  }};
  color: ${props => {
    switch(props.$status) {
      case 'published': return '#155724';
      case 'draft': return '#856404';
      case 'archived': return '#721c24';
      default: return '#495057';
    }
  }};
`;

const AnnouncementsPage = () => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    archived: 0,
    pinned: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });
  const [selectedAnnouncements, setSelectedAnnouncements] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState(false);
  const [batchDeleteProgress, setBatchDeleteProgress] = useState({ current: 0, total: 0, deleting: false });
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'notice',
    priority: 'medium',
    status: 'draft',
    isTop: false,
    isPinned: false,
    publishAt: '',
    expireAt: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [selectFilter, setSelectFilter] = useState('all');

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchAnnouncements = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);
      if (filters.search) params.append('search', filters.search);
      params.append('page', pagination.page);
      params.append('pageSize', pagination.pageSize);

      const response = await axios.get(`/api/admin/announcements?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      setAnnouncements(response.data.announcements || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0,
        totalPages: response.data.totalPages || 0
      }));
    } catch (err) {
      console.error('获取公告列表失败:', err);
      setError('获取公告列表失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/admin/announcements/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      setStats(response.data.stats || {
        total: 0,
        published: 0,
        draft: 0,
        archived: 0,
        pinned: 0
      });
    } catch (err) {
      console.error('获取公告统计失败:', err);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.page]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedAnnouncements(announcements.map(a => a._id));
    } else {
      setSelectedAnnouncements([]);
    }
  };

  const handleSelect = (id) => {
    if (selectedAnnouncements.includes(id)) {
      setSelectedAnnouncements(selectedAnnouncements.filter(i => i !== id));
    } else {
      setSelectedAnnouncements([...selectedAnnouncements, id]);
    }
  };

  const handleBatchPublish = async () => {
    if (selectedAnnouncements.length === 0) {
      showToast('请先选择要发布的公告', 'error');
      return;
    }

    try {
      for (const id of selectedAnnouncements) {
        await axios.put(`/api/admin/announcements/${id}/publish`, {}, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` }
        });
      }
      showToast(`已发布 ${selectedAnnouncements.length} 条公告`);
      setSelectedAnnouncements([]);
      fetchAnnouncements();
      fetchStats();
    } catch (err) {
      console.error('批量发布失败:', err);
      showToast('批量发布失败', 'error');
    }
  };

  // 一键发布所有草稿公告
  const handlePublishAll = async () => {
    try {
      const response = await axios.post('/api/admin/announcements/publish-all', {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` }
      });
      showToast(response.data.message || '已发布所有草稿公告');
      fetchAnnouncements();
      fetchStats();
    } catch (err) {
      console.error('一键发布失败:', err);
      showToast('一键发布失败', 'error');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedAnnouncements.length === 0) {
      showToast('请先选择要删除的公告', 'error');
      return;
    }

    setShowBatchDeleteModal(true);
    setBatchDeleteProgress({ current: 0, total: selectedAnnouncements.length, deleting: false });
  };

  const confirmBatchDelete = async () => {
    if (selectedAnnouncements.length === 0) return;

    setBatchDeleteProgress({ current: 0, total: selectedAnnouncements.length, deleting: true });

    try {
      // 分批删除，每批10条
      const batchSize = 10;
      let deletedCount = 0;
      const totalBatches = Math.ceil(selectedAnnouncements.length / batchSize);

      for (let i = 0; i < totalBatches; i++) {
        const batch = selectedAnnouncements.slice(i * batchSize, (i + 1) * batchSize);
        
        const response = await axios.post('/api/admin/announcements/batch-delete',
          { ids: batch },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        deletedCount += response.data.deletedCount || batch.length;
        setBatchDeleteProgress({ 
          current: deletedCount, 
          total: selectedAnnouncements.length, 
          deleting: true 
        });
      }

      setShowBatchDeleteModal(false);
      showToast(`成功删除 ${deletedCount} 条公告`);
      setSelectedAnnouncements([]);
      setSelectFilter('all');
      fetchAnnouncements();
      fetchStats();
    } catch (err) {
      console.error('批量删除失败:', err);
      showToast(err.response?.data?.message || '批量删除失败', 'error');
      setShowBatchDeleteModal(false);
    }
  };

  // 快捷键支持
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + A: 全选当前页
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
        e.preventDefault();
        setSelectedAnnouncements(announcements.map(a => a._id));
      }
      // Delete: 批量删除选中项
      if (e.key === 'Delete' && selectedAnnouncements.length > 0 && !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
        e.preventDefault();
        handleBatchDelete();
      }
      // Escape: 取消选择
      if (e.key === 'Escape' && selectedAnnouncements.length > 0) {
        setSelectedAnnouncements([]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcements, selectedAnnouncements]);

  // 按状态筛选选中项
  const handleSelectByStatus = (status) => {
    if (status === 'all') {
      setSelectedAnnouncements(announcements.map(a => a._id));
    } else {
      setSelectedAnnouncements(announcements.filter(a => a.status === status).map(a => a._id));
    }
    setSelectFilter(status);
  };

  const handleGenerateTestData = async () => {
    try {
      const response = await axios.post('/api/admin/announcements/seed', {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.data.success) {
        showToast(response.data.message);
        fetchAnnouncements();
        fetchStats();
      } else {
        showToast(response.data.message, 'error');
      }
    } catch (err) {
      console.error('生成测试数据失败:', err);
      showToast('生成测试数据失败', 'error');
    }
  };

  const handleClearDemoData = async () => {
    if (!window.confirm('确定要清空所有演示数据吗？')) return;
    try {
      const response = await axios.delete('/api/admin/announcements/demo', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      if (response.data.success) {
        showToast(response.data.message);
        fetchAnnouncements();
        fetchStats();
      } else {
        showToast(response.data.message, 'error');
      }
    } catch (err) {
      console.error('清空演示数据失败:', err);
      showToast('清空演示数据失败', 'error');
    }
  };

  const handleExportCSV = () => {
    const headers = ['标题', '内容', '类型', '优先级', '状态', '置顶', '浏览次数', '创建者', '创建时间', '发布时间'];
    const csvData = announcements.map(a => [
      a.title,
      a.content.replace(/"/g, '""'),
      getTypeText(a.type),
      getPriorityText(a.priority),
      getStatusText(a.status),
      a.isTop ? '是' : '否',
      a.viewCount || 0,
      a.createdBy?.username || '未知',
      new Date(a.createdAt).toLocaleString(),
      a.publishAt ? new Date(a.publishAt).toLocaleString() : ''
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `announcements_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    showToast('导出成功');
  };

  const handleCreate = () => {
    setIsEditing(false);
    setFormData({
      title: '',
      content: '',
      type: 'notice',
      priority: 'medium',
      status: 'draft',
      isTop: false,
      isPinned: false,
      publishAt: '',
      expireAt: ''
    });
    setShowModal(true);
  };

  const handleEdit = (announcement) => {
    setIsEditing(true);
    setSelectedAnnouncement(announcement);
    setFormData({
      title: announcement.title || '',
      content: announcement.content || '',
      type: announcement.type || 'notice',
      priority: announcement.priority || 'medium',
      status: announcement.status || 'draft',
      isTop: announcement.isTop || false,
      isPinned: announcement.isPinned || false,
      publishAt: announcement.publishAt ? announcement.publishAt.split('T')[0] : '',
      expireAt: announcement.expireAt ? announcement.expireAt.split('T')[0] : ''
    });
    setShowModal(true);
  };

  const handleViewDetail = (announcement) => {
    setSelectedAnnouncement(announcement);
    setShowDetailModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这条公告吗？')) return;

    try {
      await axios.delete(`/api/admin/announcements/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      showToast('公告删除成功');
      fetchAnnouncements();
      fetchStats();
      window.location.reload();
    } catch (err) {
      console.error('删除公告失败:', err);
      showToast('删除公告失败', 'error');
    }
  };

  const handleToggleTop = async (announcement) => {
    try {
      await axios.put(`/api/admin/announcements/${announcement._id}/toggle-top`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      showToast(announcement.isTop ? '公告已取消置顶' : '公告已置顶');
      fetchAnnouncements();
      fetchStats();
    } catch (err) {
      console.error('切换置顶状态失败:', err);
      showToast('操作失败', 'error');
    }
  };

  const handlePublish = async (announcement) => {
    try {
      await axios.put(`/api/admin/announcements/${announcement._id}/publish`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      showToast('公告发布成功');
      fetchAnnouncements();
      fetchStats();
    } catch (err) {
      console.error('发布公告失败:', err);
      showToast('发布公告失败', 'error');
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      showToast('请输入公告标题', 'error');
      return;
    }
    if (!formData.content.trim()) {
      showToast('请输入公告内容', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        publishAt: formData.publishAt ? new Date(formData.publishAt) : null,
        expireAt: formData.expireAt ? new Date(formData.expireAt) : null
      };

      if (isEditing) {
        await axios.put(`/api/admin/announcements/${selectedAnnouncement._id}`, submitData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
            'Content-Type': 'application/json'
          }
        });
        showToast('公告更新成功');
      } else {
        await axios.post('/api/admin/announcements', submitData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
            'Content-Type': 'application/json'
          }
        });
        showToast('公告创建成功');
      }

      setShowModal(false);
      fetchAnnouncements();
      fetchStats();
    } catch (err) {
      console.error('保存公告失败:', err);
      showToast('保存公告失败', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    handleFilterChange('search', e.target.search.value);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      published: '已发布',
      draft: '草稿',
      archived: '已归档'
    };
    return statusMap[status] || status;
  };

  const getTypeText = (type) => {
    const typeMap = {
      notice: '通知',
      news: '新闻',
      activity: '活动',
      system: '系统'
    };
    return typeMap[type] || type;
  };

  const getPriorityText = (priority) => {
    const priorityMap = {
      low: '低',
      medium: '中',
      high: '高'
    };
    return priorityMap[priority] || priority;
  };

  const allSelected = announcements.length > 0 && selectedAnnouncements.length === announcements.length;

  if (loading && announcements.length === 0) {
    return (
      <Container>
        <Header>
          <Title>公告管理</Title>
        </Header>
        <LoadingState>加载中...</LoadingState>
        {toast && <Toast $type={toast.type}>{toast.message}</Toast>}
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Header>
          <Title>公告管理</Title>
        </Header>
        <ErrorState>{error}</ErrorState>
        <Button onClick={fetchAnnouncements}>重试</Button>
        {toast && <Toast $type={toast.type}>{toast.message}</Toast>}
      </Container>
    );
  }

  return (
    <AdminLayout title="公告管理">
      <Container>
        {toast && <Toast $type={toast.type}>{toast.message}</Toast>}

        <Breadcrumb>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }}>仪表盘</a> / 
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/users'); }}>用户管理</a> / 公告管理
          </Breadcrumb>

          <Header>
            <Title>公告管理</Title>
            <HeaderActions>
              <Button success onClick={handleGenerateTestData}>生成测试数据</Button>
              <Button danger onClick={handleClearDemoData}>清空演示数据</Button>
              <Button primary onClick={handleCreate}>新建公告</Button>
              <Button onClick={handleExportCSV}>导出CSV</Button>
              <Button onClick={() => { fetchAnnouncements(); fetchStats(); }}>刷新</Button>
            </HeaderActions>
          </Header>

      <StatsContainer>
        <StatCard onClick={() => handleFilterChange('status', '')} active={filters.status === ''}>
          <StatValue>{stats.total}</StatValue>
          <StatLabel>全部公告</StatLabel>
        </StatCard>
        <StatCard onClick={() => handleFilterChange('status', 'published')} active={filters.status === 'published'}>
          <StatValue style={{ color: '#28a745' }}>{stats.published}</StatValue>
          <StatLabel>已发布</StatLabel>
        </StatCard>
        <StatCard onClick={() => handleFilterChange('status', 'draft')} active={filters.status === 'draft'}>
          <StatValue style={{ color: '#ffc107' }}>{stats.draft}</StatValue>
          <StatLabel>草稿</StatLabel>
        </StatCard>
        <StatCard onClick={() => handleFilterChange('status', 'archived')} active={filters.status === 'archived'}>
          <StatValue style={{ color: '#6c757d' }}>{stats.archived}</StatValue>
          <StatLabel>已归档</StatLabel>
        </StatCard>
        <StatCard onClick={() => handleFilterChange('status', '')} active={false}>
          <StatValue style={{ color: '#dc3545' }}>{stats.pinned}</StatValue>
          <StatLabel>置顶公告</StatLabel>
        </StatCard>
      </StatsContainer>

      <FilterContainer>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.5rem', flex: 1 }}>
          <SearchInput
            type="text"
            name="search"
            placeholder="搜索公告标题或内容..."
            defaultValue={filters.search}
          />
          <Button type="submit">搜索</Button>
        </form>
        <FilterSelect
          value={filters.type}
          onChange={(e) => handleFilterChange('type', e.target.value)}
        >
          <option value="">全部类型</option>
          <option value="notice">通知</option>
          <option value="news">新闻</option>
          <option value="activity">活动</option>
          <option value="system">系统</option>
        </FilterSelect>
      </FilterContainer>

      {selectedAnnouncements.length > 0 && (
        <BatchActions>
          <SelectedCount>已选择 {selectedAnnouncements.length} 项</SelectedCount>
          
          {/* 按状态快速选择 */}
          <span style={{ fontSize: '0.85rem', color: '#666' }}>快速选择:</span>
          <SelectionDropdown value={selectFilter} onChange={(e) => handleSelectByStatus(e.target.value)}>
            <option value="all">全部 {announcements.length} 条</option>
            <option value="published">已发布 ({announcements.filter(a => a.status === 'published').length})</option>
            <option value="draft">草稿 ({announcements.filter(a => a.status === 'draft').length})</option>
            <option value="archived">已归档 ({announcements.filter(a => a.status === 'archived').length})</option>
          </SelectionDropdown>
          
          <Button success onClick={handleBatchPublish}>批量发布</Button>
          <Button primary onClick={handlePublishAll}>🚀 一键发布全部</Button>
          <Button danger onClick={handleBatchDelete}>🗑️ 批量删除</Button>
          <Button onClick={() => { setSelectedAnnouncements([]); setSelectFilter('all'); }}>取消选择</Button>
          
          {/* 快捷键提示 */}
          <span style={{ fontSize: '0.75rem', color: '#999', marginLeft: '0.5rem' }}>
            快捷键: Ctrl+A 全选 | Delete 删除 | Esc 取消
          </span>
        </BatchActions>
      )}

      <AnnouncementList>
        <SelectAllContainer>
          <Checkbox
            type="checkbox"
            checked={allSelected}
            onChange={handleSelectAll}
          />
          <span>全选</span>
        </SelectAllContainer>
        {announcements.length === 0 ? (
          <EmptyState>
            <p style={{ fontSize: '2rem', marginBottom: '1rem' }}>📢</p>
            <p>暂无公告记录</p>
            <Button primary onClick={handleCreate} style={{ marginTop: '1rem' }}>新建公告</Button>
          </EmptyState>
        ) : (
          announcements.map((announcement) => (
            <AnnouncementItem key={announcement._id} selected={selectedAnnouncements.includes(announcement._id)}>
              <Checkbox
                type="checkbox"
                checked={selectedAnnouncements.includes(announcement._id)}
                onChange={() => handleSelect(announcement._id)}
              />
              <AnnouncementContent>
                <AnnouncementHeader>
                  <AnnouncementTitle>
                    {announcement.title}
                    <TypeBadge $type={announcement.type}>{getTypeText(announcement.type)}</TypeBadge>
                    {announcement.isTop && <TopBadge>置顶</TopBadge>}
                  </AnnouncementTitle>
                  <AnnouncementStatus $status={announcement.status}>
                    {getStatusText(announcement.status)}
                  </AnnouncementStatus>
                </AnnouncementHeader>

                <AnnouncementDescription>
                  {announcement.content.length > 150
                    ? announcement.content.substring(0, 150) + '...'
                    : announcement.content}
                </AnnouncementDescription>

                <AnnouncementMeta>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span>优先级: {getPriorityText(announcement.priority)}</span>
                    <span>|</span>
                    <span>浏览: {announcement.viewCount || 0}</span>
                    <span>|</span>
                    <span>创建者: {announcement.createdBy?.username || '未知'}</span>
                  </div>
                  <span>{new Date(announcement.createdAt).toLocaleString()}</span>
                </AnnouncementMeta>

                <AnnouncementActions>
                  <ActionButton className="primary" onClick={() => handleViewDetail(announcement)}>
                    查看详情
                  </ActionButton>
                  <ActionButton onClick={() => handleEdit(announcement)}>
                    编辑
                  </ActionButton>
                  <ActionButton className="warning" onClick={() => handleToggleTop(announcement)}>
                    {announcement.isTop ? '取消置顶' : '置顶'}
                  </ActionButton>
                  {announcement.status !== 'published' && (
                    <ActionButton className="success" onClick={() => handlePublish(announcement)}>
                      发布
                    </ActionButton>
                  )}
                  <ActionButton className="danger" onClick={() => handleDelete(announcement._id)}>
                    删除
                  </ActionButton>
                </AnnouncementActions>
              </AnnouncementContent>
            </AnnouncementItem>
          ))
        )}
      </AnnouncementList>

      {pagination.totalPages > 1 && (
        <Pagination>
          <PageButton onClick={() => handlePageChange(1)} disabled={pagination.page === 1}>首页</PageButton>
          <PageButton onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1}>上一页</PageButton>
          <span style={{ padding: '0.5rem' }}>第 {pagination.page} / {pagination.totalPages} 页</span>
          <PageButton onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === pagination.totalPages}>下一页</PageButton>
          <PageButton onClick={() => handlePageChange(pagination.totalPages)} disabled={pagination.page === pagination.totalPages}>末页</PageButton>
        </Pagination>
      )}

      {showModal && (
        <Modal onClick={() => setShowModal(false)}>
          <ModalContent $size="large" onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{isEditing ? '编辑公告' : '新建公告'}</ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <FormLabel>公告标题 *</FormLabel>
                <FormInput
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="请输入公告标题"
                  maxLength={200}
                />
              </FormGroup>
              <FormRow>
                <FormCol>
                  <FormGroup>
                    <FormLabel>公告类型</FormLabel>
                    <FilterSelect
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      style={{ width: '100%' }}
                    >
                      <option value="notice">通知</option>
                      <option value="news">新闻</option>
                      <option value="activity">活动</option>
                      <option value="system">系统</option>
                    </FilterSelect>
                  </FormGroup>
                </FormCol>
                <FormCol>
                  <FormGroup>
                    <FormLabel>优先级</FormLabel>
                    <FilterSelect
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      style={{ width: '100%' }}
                    >
                      <option value="low">低</option>
                      <option value="medium">中</option>
                      <option value="high">高</option>
                    </FilterSelect>
                  </FormGroup>
                </FormCol>
              </FormRow>
              <FormRow>
                <FormCol>
                  <FormGroup>
                    <FormLabel>发布时间</FormLabel>
                    <FormInput
                      type="date"
                      value={formData.publishAt}
                      onChange={(e) => setFormData({ ...formData, publishAt: e.target.value })}
                    />
                  </FormGroup>
                </FormCol>
                <FormCol>
                  <FormGroup>
                    <FormLabel>过期时间</FormLabel>
                    <FormInput
                      type="date"
                      value={formData.expireAt}
                      onChange={(e) => setFormData({ ...formData, expireAt: e.target.value })}
                    />
                  </FormGroup>
                </FormCol>
              </FormRow>
              <FormGroup>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={formData.isTop}
                    onChange={(e) => setFormData({ ...formData, isTop: e.target.checked })}
                  />
                  置顶公告
                </CheckboxLabel>
              </FormGroup>
              <FormGroup>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={formData.isPinned}
                    onChange={(e) => setFormData({ ...formData, isPinned: e.target.checked })}
                  />
                  固定显示
                </CheckboxLabel>
              </FormGroup>
              <FormGroup>
                <FormLabel>公告内容 *</FormLabel>
                <QuillEditorWrapper>
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={(content) => setFormData({ ...formData, content })}
                    placeholder="请输入公告内容"
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        [{ 'color': [] }, { 'background': [] }],
                        [{ 'align': [] }],
                        ['image', 'link'],
                        ['clean']
                      ]
                    }}
                  />
                </QuillEditorWrapper>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setShowModal(false)}>取消</Button>
              <Button primary onClick={handleSubmit} disabled={submitting}>
                {submitting ? '保存中...' : '保存'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {showDetailModal && selectedAnnouncement && (
        <Modal onClick={() => setShowDetailModal(false)}>
          <ModalContent $size="large" onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>公告详情</ModalTitle>
              <CloseButton onClick={() => setShowDetailModal(false)}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <FormLabel>标题</FormLabel>
                <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{selectedAnnouncement.title}</div>
              </FormGroup>
              <FormRow>
                <FormCol>
                  <FormGroup>
                    <FormLabel>类型</FormLabel>
                    <TypeBadge $type={selectedAnnouncement.type}>{getTypeText(selectedAnnouncement.type)}</TypeBadge>
                  </FormGroup>
                </FormCol>
                <FormCol>
                  <FormGroup>
                    <FormLabel>状态</FormLabel>
                    <AnnouncementStatus $status={selectedAnnouncement.status}>
                      {getStatusText(selectedAnnouncement.status)}
                    </AnnouncementStatus>
                  </FormGroup>
                </FormCol>
                <FormCol>
                  <FormGroup>
                    <FormLabel>优先级</FormLabel>
                    <div>{getPriorityText(selectedAnnouncement.priority)}</div>
                  </FormGroup>
                </FormCol>
              </FormRow>
              <FormRow>
                <FormCol>
                  <FormGroup>
                    <FormLabel>浏览次数</FormLabel>
                    <div>{selectedAnnouncement.viewCount || 0}</div>
                  </FormGroup>
                </FormCol>
                <FormCol>
                  <FormGroup>
                    <FormLabel>置顶</FormLabel>
                    <div>{selectedAnnouncement.isTop ? '是' : '否'}</div>
                  </FormGroup>
                </FormCol>
                <FormCol>
                  <FormGroup>
                    <FormLabel>固定</FormLabel>
                    <div>{selectedAnnouncement.isPinned ? '是' : '否'}</div>
                  </FormGroup>
                </FormCol>
              </FormRow>
              <FormRow>
                <FormCol>
                  <FormGroup>
                    <FormLabel>创建者</FormLabel>
                    <div>{selectedAnnouncement.createdBy?.username || '未知'}</div>
                  </FormGroup>
                </FormCol>
                <FormCol>
                  <FormGroup>
                    <FormLabel>最后更新者</FormLabel>
                    <div>{selectedAnnouncement.updatedBy?.username || '未知'}</div>
                  </FormGroup>
                </FormCol>
              </FormRow>
              <FormRow>
                <FormCol>
                  <FormGroup>
                    <FormLabel>创建时间</FormLabel>
                    <div>{new Date(selectedAnnouncement.createdAt).toLocaleString()}</div>
                  </FormGroup>
                </FormCol>
                <FormCol>
                  <FormGroup>
                    <FormLabel>更新时间</FormLabel>
                    <div>{new Date(selectedAnnouncement.updatedAt).toLocaleString()}</div>
                  </FormGroup>
                </FormCol>
              </FormRow>
              {selectedAnnouncement.publishAt && (
                <FormGroup>
                  <FormLabel>发布时间</FormLabel>
                  <div>{new Date(selectedAnnouncement.publishAt).toLocaleString()}</div>
                </FormGroup>
              )}
              {selectedAnnouncement.expireAt && (
                <FormGroup>
                  <FormLabel>过期时间</FormLabel>
                  <div>{new Date(selectedAnnouncement.expireAt).toLocaleString()}</div>
                </FormGroup>
              )}
              <FormGroup>
                <FormLabel>内容</FormLabel>
                <ContentPreview dangerouslySetInnerHTML={{ __html: selectedAnnouncement.content }} />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setShowDetailModal(false)}>关闭</Button>
              <Button primary onClick={() => {
                setShowDetailModal(false);
                handleEdit(selectedAnnouncement);
              }}>编辑</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* 批量删除确认模态框 */}
      {showBatchDeleteModal && (
        <Modal onClick={() => !batchDeleteProgress.deleting && setShowBatchDeleteModal(false)}>
          <ConfirmModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>确认删除</ModalTitle>
              {!batchDeleteProgress.deleting && (
                <CloseButton onClick={() => setShowBatchDeleteModal(false)}>&times;</CloseButton>
              )}
            </ModalHeader>
            <ModalBody>
              <WarningIcon>⚠️</WarningIcon>
              
              {!batchDeleteProgress.deleting ? (
                <>
                  <ConfirmMessage>
                    确定要删除选中的 <strong>{selectedAnnouncements.length}</strong> 条公告吗？
                  </ConfirmMessage>
                  <p style={{ textAlign: 'center', color: '#dc3545', marginBottom: '1rem' }}>
                    此操作不可撤销，删除后数据将无法恢复！
                  </p>
                  
                  <ConfirmDetails>
                    <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#333' }}>
                      即将删除的公告列表：
                    </div>
                    {announcements
                      .filter(a => selectedAnnouncements.includes(a._id))
                      .slice(0, 10)
                      .map(a => (
                        <ConfirmDetailItem key={a._id}>
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {a.title}
                          </span>
                          <StatusBadge $status={a.status} style={{ marginLeft: '0.5rem', flexShrink: 0 }}>
                            {getStatusText(a.status)}
                          </StatusBadge>
                        </ConfirmDetailItem>
                      ))}
                    {selectedAnnouncements.length > 10 && (
                      <div style={{ textAlign: 'center', color: '#666', padding: '0.5rem', fontSize: '0.85rem' }}>
                        ...还有 {selectedAnnouncements.length - 10} 条未显示
                      </div>
                    )}
                  </ConfirmDetails>
                </>
              ) : (
                <DeleteProgress>
                  <ConfirmMessage>正在删除...</ConfirmMessage>
                  <ProgressBar>
                    <ProgressFill percent={Math.round((batchDeleteProgress.current / batchDeleteProgress.total) * 100)} />
                  </ProgressBar>
                  <ProgressText>
                    {batchDeleteProgress.current} / {batchDeleteProgress.total} 条已删除
                  </ProgressText>
                </DeleteProgress>
              )}
            </ModalBody>
            <ModalFooter>
              {!batchDeleteProgress.deleting && (
                <>
                  <Button onClick={() => setShowBatchDeleteModal(false)}>取消</Button>
                  <Button danger onClick={confirmBatchDelete}>确认删除</Button>
                </>
              )}
            </ModalFooter>
          </ConfirmModalContent>
        </Modal>
      )}
    </Container>
    </AdminLayout>
  );
};

export default AnnouncementsPage;